import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Trash2, Loader2, GripVertical, Eye } from "lucide-react";
import { toast } from "sonner";
import { TrackingPromoSlider } from "@/components/menu/TrackingPromoSlider";

interface Slide {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  is_active: boolean;
  sort_order: number;
}

export const TrackingSlidesManager = ({ restaurantId }: { restaurantId: string }) => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newImage, setNewImage] = useState("");
  const [newLink, setNewLink] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    const { data } = await supabase
      .from("tracking_slides" as any)
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("sort_order") as any;
    setSlides((data || []) as Slide[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [restaurantId]);

  const addSlide = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    await (supabase.from("tracking_slides" as any) as any).insert({
      restaurant_id: restaurantId,
      title: newTitle.trim(),
      description: newDesc.trim() || null,
      image_url: newImage.trim() || null,
      link_url: newLink.trim() || null,
      sort_order: slides.length,
    });
    setNewTitle(""); setNewDesc(""); setNewImage(""); setNewLink("");
    await load();
    setSaving(false);
    toast.success("Slide adicionado!");
  };

  const removeSlide = async (id: string) => {
    await (supabase.from("tracking_slides" as any) as any).delete().eq("id", id);
    await load();
    toast.success("Slide removido");
  };

  const toggleActive = async (id: string, active: boolean) => {
    await (supabase.from("tracking_slides" as any) as any).update({ is_active: !active }).eq("id", id);
    await load();
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const activeSlides = slides.filter(s => s.is_active);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plus className="h-5 w-5 text-primary" /> Adicionar Slide
            </CardTitle>
            <CardDescription>Slides promocionais exibidos na página de acompanhamento do pedido</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Título do slide *" value={newTitle} onChange={e => setNewTitle(e.target.value)} />
            <Input placeholder="Descrição (opcional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
            <Input placeholder="URL da imagem (opcional)" value={newImage} onChange={e => setNewImage(e.target.value)} />
            <Input placeholder="Link ao clicar (opcional)" value={newLink} onChange={e => setNewLink(e.target.value)} />
            <Button onClick={addSlide} disabled={saving || !newTitle.trim()} className="w-full gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Adicionar
            </Button>
          </CardContent>
        </Card>

        {slides.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Slides cadastrados ({slides.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {slides.map(slide => (
                <div key={slide.id} className="flex items-center gap-3 p-3 rounded-xl border bg-card">
                  <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                  {slide.image_url && <img src={slide.image_url} alt="" className="w-12 h-8 rounded object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{slide.title}</p>
                    {slide.description && <p className="text-xs text-muted-foreground truncate">{slide.description}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(slide.id, slide.is_active)} className={slide.is_active ? "text-green-600" : "text-muted-foreground"}>
                    {slide.is_active ? "Ativo" : "Inativo"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeSlide(slide.id)} className="text-destructive h-8 w-8">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Preview */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-5 w-5 text-primary" /> Preview
            </CardTitle>
            <CardDescription>Assim seus clientes verão na tela de acompanhamento</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-[360px] mx-auto bg-[hsl(0,0%,97%)] rounded-2xl p-4 space-y-3">
              {/* Fake tracking header */}
              <div className="bg-white rounded-xl p-3 shadow-sm">
                <p className="font-bold text-sm text-[hsl(0,0%,13%)]">Seu pedido está sendo preparado</p>
                <div className="flex gap-1 mt-2">
                  <div className="flex-1 h-1 rounded-full bg-orange-500" />
                  <div className="flex-1 h-1 rounded-full bg-amber-400" />
                  <div className="flex-1 h-1 rounded-full bg-[hsl(0,0%,90%)]" />
                  <div className="flex-1 h-1 rounded-full bg-[hsl(0,0%,90%)]" />
                </div>
                <p className="text-xs text-[hsl(0,0%,55%)] mt-2">Previsão: 25 minutos</p>
              </div>

              {activeSlides.length > 0 ? (
                <TrackingPromoSlider slides={activeSlides} primaryColor="#f97316" />
              ) : (
                <div className="rounded-xl border-2 border-dashed border-[hsl(0,0%,85%)] p-6 text-center">
                  <p className="text-xs text-muted-foreground">Adicione slides para ver o preview</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
